// src/oura/lib/scanAudit.ts
//
// What the staff audit badge says, and how its list is grouped (#4242).
//
// Every finished scan, and every transplant, is audited on the server
// (ourteam-backend#5910). The result is read-only at
// GET /api/v1/assessments/{id}/audit and staff-only. The point of showing it is
// that a problem is found by the system, not by the boss or the client.
//
// Pure, so the wording and the grouping can be checked without a screen.

import type { AeoScanAudit, AeoScanAuditIssue, AeoScanAuditNote } from "@service/aeo";
import { discoveredDateTime } from "./oura";

/** How often to ask again while the audit has not landed, in ms. */
export const AUDIT_POLL_MS = 5_000;
/** The backend's own wait is 300 s (then it stores `error`); a few seconds over, so that is read too. */
export const AUDIT_MAX_WAIT_MS = 310_000;

/**
 * Ask again? Only while the audit says `not_audited` AND the scan finished within
 * the backend's wait. The audit starts in a background thread after a scan
 * completes, so a read just then can be `not_audited` and be right a moment
 * later; a scan from before the audit existed is `not_audited` forever, and is
 * not polled. An unreadable `scanAt` means one read only.
 */
export function shouldPollAudit(status: AeoScanAudit["status"] | undefined, scanAt: string | null | undefined, now: number): boolean {
    if (status !== "not_audited" || !scanAt) return false;
    const finished = Date.parse(scanAt);
    return !Number.isNaN(finished) && now - finished < AUDIT_MAX_WAIT_MS;
}

/** An audit tied to the scan it belongs to. */
export interface AuditResult {
    assessmentId: string;
    audit: AeoScanAudit | null;
}

/**
 * The audit to show for the scan asked about NOW. Another scan's result, still
 * in state for the one commit after the scan changed, is never shown under it.
 */
export function auditFor(result: AuditResult | null, assessmentId: string | undefined, enabled: boolean): AeoScanAudit | null {
    if (!enabled || !assessmentId || result?.assessmentId !== assessmentId) return null;
    return result.audit;
}

export type AuditTone = "green" | "amber" | "gray";

export interface AuditBadge {
    tone: AuditTone;
    label: string;
    /** The tooltip: when it was checked and what the audit tidied up by itself. */
    title: string;
    /** There is a list to open: issues, or notes. A clean audit with neither has none. */
    hasList: boolean;
}

/** An issue the list can show. A malformed row is dropped, never drawn as a blank line. */
const usableIssue = (i: AeoScanAuditIssue) => !!i && typeof i.n === "number" && typeof i.rule === "string" && i.rule.trim() !== "";
const usableNote = (n: AeoScanAuditNote) => !!n && typeof n.n === "number" && typeof n.note === "string" && n.note.trim() !== "";

export const usableIssues = (audit: AeoScanAudit): AeoScanAuditIssue[] => audit.issues.filter(usableIssue);
export const usableNotes = (audit: AeoScanAudit): AeoScanAuditNote[] => audit.notes.filter(usableNote);

function checkedLine(checkedAt: string | null): string | null {
    if (!checkedAt) return null;
    const d = new Date(checkedAt);
    return Number.isNaN(d.getTime()) ? null : `Checked ${discoveredDateTime(checkedAt)}`;
}

/**
 * The badge for one audit:
 *   clean               green  "Audit clean"
 *   issues              amber  "N issues"  (N is the number of issues)
 *   not_audited / error grey   "Not audited" / "Audit error"
 *
 * `issues` with no usable issue in the list is still "issues": the backend said
 * so, and the count is the number of rows it sent, not a number this screen
 * second-guesses. It reads "Audit found issues" then, rather than "0 issues".
 */
export function auditBadge(audit: AeoScanAudit): AuditBadge {
    const lines = [
        checkedLine(audit.checkedAt),
        audit.fixed.length > 0 ? `Tidied by the audit: ${audit.fixed.join("; ")}` : null,
    ].filter((l): l is string => l !== null);
    const title = lines.join(". ");
    const hasNotes = usableNotes(audit).length > 0;
    const count = usableIssues(audit).length;

    switch (audit.status) {
        case "clean":
            return { tone: "green", label: "Audit clean", title, hasList: hasNotes };
        case "issues":
            return {
                tone: "amber",
                label: count > 0 ? `${count} issue${count === 1 ? "" : "s"}` : "Audit found issues",
                title,
                hasList: count > 0 || hasNotes,
            };
        case "error":
            return { tone: "gray", label: "Audit error", title: title || "The audit could not finish for this scan.", hasList: count > 0 || hasNotes };
        case "not_audited":
        default:
            return { tone: "gray", label: "Not audited", title: title || "This scan has not been audited.", hasList: false };
    }
}

export interface AuditGroup<T> {
    n: number;
    /** "Scan" for 0, otherwise "Q{n}" (the question's text is added by the caller). */
    heading: string;
    rows: T[];
}

/** Rows grouped by question: the scan as a whole (0) first, then questions in order. */
export function groupByQuestion<T extends { n: number }>(rows: T[]): AuditGroup<T>[] {
    const byN = new Map<number, T[]>();
    rows.forEach((r) => byN.set(r.n, [...(byN.get(r.n) ?? []), r]));
    return Array.from(byN.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([n, list]) => ({ n, heading: n === 0 ? "Scan" : `Q${n}`, rows: list }));
}
